import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { t, getLocale } from '@/i18n';
import { ReportHeader } from './ReportHeader';
import { ReportComment } from './ReportComment';

const fmt = (n, digits = 1) => n.toLocaleString(getLocale(), { maximumFractionDigits: digits });
const dayLabel = (iso) => new Date(iso).toLocaleDateString(getLocale(), { day: 'numeric', month: 'numeric' });

// One KPI line: this week, before the pilot and whether it improved.
const KpiRow = ({ label, now, before, unit, lowerIsBetter }) => {
  const better = lowerIsBetter ? now < before : now > before;
  return (
    <tr className="border-t">
      <td className="py-2 pr-2">{label}</td>
      <td className={cn('py-2 px-2 text-right font-semibold tabular-nums', better ? 'text-emerald-700' : 'text-red-700')}>
        {fmt(now)} {unit}
      </td>
      <td className="py-2 pl-2 text-right tabular-nums text-muted-foreground">{fmt(before)} {unit}</td>
    </tr>
  );
};

// Preview of the weekly report for plant management (Monday morning).
export const WeeklyReport = ({ report }) => {
  const { kpis, worst } = report;
  return (
    <Card className="p-4 space-y-4">
      <ReportHeader
        title={t('reports.weekly.title')}
        schedule={t('reports.weekly.schedule')}
        period={`${dayLabel(report.from)} – ${dayLabel(report.to)}`}
      />
      <ReportComment kind="weekly" report={report} />
      <table className="w-full text-sm">
        <thead className="text-xs text-muted-foreground">
          <tr>
            <th className="text-left font-medium pb-1" />
            <th className="text-right font-medium pb-1 px-2">{t('reports.weekly.thisWeek')}</th>
            <th className="text-right font-medium pb-1 pl-2">{t('stats.beforePilot')}</th>
          </tr>
        </thead>
        <tbody>
          <KpiRow label={t('stats.kpi.noShows')} now={kpis.noShows.now} before={kpis.noShows.before} unit={t('stats.unit.perDay')} lowerIsBetter />
          <KpiRow label={t('stats.kpi.informed')} now={kpis.informedPct.now} before={kpis.informedPct.before} unit="%" />
          <KpiRow label={t('stats.kpi.parking')} now={kpis.parkingTrucks.now} before={kpis.parkingTrucks.before} unit={t('stats.unit.trucksPerNight')} lowerIsBetter />
        </tbody>
      </table>
      <div className="space-y-1">
        <div className="text-xs font-semibold">{t('reports.weekly.worst')}</div>
        <ol className="text-sm divide-y">
          {worst.map(({ carrier, now }, i) => (
            <li key={carrier.id} className="py-1.5 flex items-baseline gap-2">
              <span className="text-xs text-muted-foreground w-4">{i + 1}.</span>
              <span className="min-w-0 flex-1 truncate font-medium">{carrier.name}</span>
              <span className="text-xs tabular-nums text-red-700 whitespace-nowrap">
                {fmt(now.noShowPct, 0)} % {t('reports.weekly.noShowShort')}
              </span>
            </li>
          ))}
        </ol>
      </div>
    </Card>
  );
};
